import { useState } from 'react';
import toast from 'react-hot-toast';

import Modal from '../../../common/components/modal/modal';
import { useRequestId } from '../../../core/network/idempotency';
import { useAgreeTier, useBreakdown } from '../contexts/breakdown';
import { readMissing, readReady } from '../utils/missing';

import '../styles/breakdown.css';

// Agreeing a tier's scope, as a modal — from the breakdown screen or straight
// from a row in the tiers list, without leaving it.
//
// IT READS THE BREAKDOWN ITSELF. What is signed is `breakdown_revision` as it
// was READ, and whether the tier can be agreed at all is the backend's
// `ready_to_agree` list: neither is in the tiers list row. The query is the
// same one the breakdown screen uses, so opening it from there costs nothing.
//
// THE MINIMUM: which tier, which revision, which plan it comes out of. What is
// missing appears only when something is, cut to a few lines, and the button
// stays off — the full checklist lives in the breakdown.

const SHOWN_MISSING = 3;

export function AgreeTierButton({ tierId, className = 'tier-row__agree', label = 'Acordar' }) {
    const [open, setOpen] = useState(false);

    return (
        <>
            <button type="button" className={className} onClick={() => setOpen(true)}>
                {label}
            </button>
            {open && <AgreeTierModal tierId={tierId} onClose={() => setOpen(false)} />}
        </>
    );
}

export default function AgreeTierModal({ tierId, onClose }) {
    const { data, isLoading, error, refetch } = useBreakdown(tierId);
    const agree = useAgreeTier(tierId);
    const keyFor = useRequestId();

    const breakdown = data?.breakdown ?? data;
    const tier = breakdown?.tier ?? breakdown;
    const revision = tier?.breakdown_revision ?? breakdown?.breakdown_revision;
    const state = tier?.breakdown_state;

    const missing = readMissing(breakdown) ?? [];
    const ready = readReady(breakdown);

    const plan = breakdown?.plan ?? tier?.plan ?? null;
    const planVersion = breakdown?.plan_version ?? tier?.plan_version ?? null;
    const planLabel = plan?.name
        ? `${plan.name}${planVersion?.number != null ? ` · v${planVersion.number}` : ''}`
        : null;

    const loaded = !isLoading && !error && !!tier;
    const draft = state === 'draft';
    const canSign = loaded && draft && ready !== false && revision != null && !agree.isPending;

    const handleAgree = async () => {
        // The SAME key while the body is the same, so a lost answer can be
        // resent without signing twice.
        const body = { request_id: keyFor({ tier: tierId, revision }), breakdown_revision: revision };

        try {
            await agree.mutateAsync(body);
            toast.success('Tier acordado');
            onClose();
        } catch (err) {
            if (err.isRevisionConflict) {
                // Stays open on the fresh read: the revision shown is now the
                // current one, and signing it is a decision taken again.
                await refetch();
                toast.error('El tier cambió mientras mirabas. Se releyó: revisá la revisión y volvé a confirmar.');
                return;
            }
            if (err.isUnconfirmed) {
                toast.error('No pudimos confirmar el resultado. Reintentá: lleva la misma clave y no se firma dos veces.');
                return;
            }
            toast.error(err.message || 'No se pudo acordar el tier');
        }
    };

    return (
        <Modal
            open
            title="Acordar tier"
            busy={agree.isPending}
            onClose={onClose}
            actions={(
                <>
                    <button
                        type="button"
                        className="breakdown__button"
                        onClick={onClose}
                        disabled={agree.isPending}
                    >
                        Cancelar
                    </button>
                    <button
                        type="button"
                        className="breakdown__button breakdown__button--primary"
                        onClick={handleAgree}
                        disabled={!canSign}
                        autoFocus
                    >
                        {agree.isPending
                            ? 'Acordando...'
                            : revision != null ? `Acordar la revisión ${revision}` : 'Acordar'}
                    </button>
                </>
            )}
        >
            {isLoading && <p className="modal__note">Leyendo el tier...</p>}

            {error && (
                <p className="modal__warn">
                    {error.status === 404
                        ? 'Este tier no está disponible: no existe, o queda fuera de tu alcance.'
                        : error.message || 'No se pudo leer el tier.'}
                </p>
            )}

            {loaded && (
                <>
                    <dl className="modal__facts">
                        <div>
                            <dt>Tier</dt>
                            <dd>{tier.name}</dd>
                        </div>
                        <div>
                            <dt>Revisión</dt>
                            <dd>{revision ?? '—'}</dd>
                        </div>
                        {planLabel && (
                            <div>
                                <dt>Sale de</dt>
                                <dd>{planLabel}</dd>
                            </div>
                        )}
                    </dl>

                    {!draft && (
                        <p className="modal__warn">
                            {state === 'agreed'
                                ? 'Este tier ya está acordado.'
                                : 'Este tier no está en borrador, así que no se acuerda.'}
                        </p>
                    )}

                    {draft && missing.length > 0 && (
                        <div className="modal__warn">
                            Todavía falta:
                            <ul className="modal__list">
                                {missing.slice(0, SHOWN_MISSING).map(item => (
                                    <li key={item.key}>{item.text}</li>
                                ))}
                                {missing.length > SHOWN_MISSING && (
                                    <li>y {missing.length - SHOWN_MISSING} más, en el desglose</li>
                                )}
                            </ul>
                        </div>
                    )}

                    {draft && ready !== false && (
                        <p className="modal__note">
                            Queda firmado con tu nombre. Editar el alcance después lo devuelve a
                            borrador.
                        </p>
                    )}
                </>
            )}
        </Modal>
    );
}
